import React from 'react';
import { RichText } from '../RichText/RichText';
import type { RichTextCtaItem, RichTextCtaType } from '../RichText/RichText';
import type { StoreBadgeCtaType } from '../StoreBadgeLink/StoreBadgeLink';
import { Badge } from '../Badge/Badge';
import { CtaButton } from '../CtaButton/CtaButton';
import type { CtaButtonColorVariant } from '../CtaButton/CtaButton';
import { Image } from '../Image/Image';
import type { ImageAlignment, OverlayPosition } from '../Image/Image';
import type { CustomIconName } from '../CustomIcon/CustomIcon';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './SplitContent.scss';

export type SplitContentMediaPosition = 'left' | 'right';

/** How the image fills the media frame: 'fill' crops it to cover, 'fit' shows it whole. */
export type SplitContentImageFit = 'fill' | 'fit';

const BUTTON_VARIANT: Record<string, CtaButtonColorVariant> = {
  'primary-button': 'primary',
  'secondary-button': 'secondary',
  'dark-button': 'contrast',
};

const isLinkCta = (type?: string): boolean => type === 'text-link' || type === 'anchor-link';

/** Flat, per-CTA props for slots 1-5. */
export interface SplitContentCtaSlotProps {
  /** @polyLabel CTA 1 Text */
  cta1Text?: string;
  /**
   * @polyType Url
   * @polyLabel CTA 1 Link
   */
  cta1Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyLabel CTA 1 Type
   */
  cta1Type?: Exclude<RichTextCtaType, StoreBadgeCtaType>;
  /**
   * @polyLabel CTA 1 ID
   * @polyTranslatable false
   */
  cta1Id?: string;
  /**
   * @polyLabel CTA 1 LD Appendix
   * @polyTranslatable false
   */
  cta1LdAppendix?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel CTA 1 Behavior
   * @polyTranslatable false
   */
  cta1Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  cta1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  cta1TargetPopupId?: string;

  /** @polyLabel CTA 2 Text */
  cta2Text?: string;
  /**
   * @polyType Url
   * @polyLabel CTA 2 Link
   */
  cta2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyLabel CTA 2 Type
   */
  cta2Type?: Exclude<RichTextCtaType, StoreBadgeCtaType>;
  /**
   * @polyLabel CTA 2 ID
   * @polyTranslatable false
   */
  cta2Id?: string;
  /**
   * @polyLabel CTA 2 LD Appendix
   * @polyTranslatable false
   */
  cta2LdAppendix?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel CTA 2 Behavior
   * @polyTranslatable false
   */
  cta2Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  cta2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  cta2TargetPopupId?: string;

  /** @polyLabel CTA 3 Text */
  cta3Text?: string;
  /**
   * @polyType Url
   * @polyLabel CTA 3 Link
   */
  cta3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyLabel CTA 3 Type
   */
  cta3Type?: Exclude<RichTextCtaType, StoreBadgeCtaType>;
  /**
   * @polyLabel CTA 3 ID
   * @polyTranslatable false
   */
  cta3Id?: string;
  /**
   * @polyLabel CTA 3 LD Appendix
   * @polyTranslatable false
   */
  cta3LdAppendix?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel CTA 3 Behavior
   * @polyTranslatable false
   */
  cta3Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  cta3Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  cta3TargetPopupId?: string;

  /** @polyLabel CTA 4 Text */
  cta4Text?: string;
  /**
   * @polyType Url
   * @polyLabel CTA 4 Link
   */
  cta4Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyLabel CTA 4 Type
   */
  cta4Type?: Exclude<RichTextCtaType, StoreBadgeCtaType>;
  /**
   * @polyLabel CTA 4 ID
   * @polyTranslatable false
   */
  cta4Id?: string;
  /**
   * @polyLabel CTA 4 LD Appendix
   * @polyTranslatable false
   */
  cta4LdAppendix?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel CTA 4 Behavior
   * @polyTranslatable false
   */
  cta4Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  cta4Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  cta4TargetPopupId?: string;

  /** @polyLabel CTA 5 Text */
  cta5Text?: string;
  /**
   * @polyType Url
   * @polyLabel CTA 5 Link
   */
  cta5Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyLabel CTA 5 Type
   */
  cta5Type?: Exclude<RichTextCtaType, StoreBadgeCtaType>;
  /**
   * @polyLabel CTA 5 ID
   * @polyTranslatable false
   */
  cta5Id?: string;
  /**
   * @polyLabel CTA 5 LD Appendix
   * @polyTranslatable false
   */
  cta5LdAppendix?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel CTA 5 Behavior
   * @polyTranslatable false
   */
  cta5Behavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  cta5Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  cta5TargetPopupId?: string;
}

/** Flat, per-item check list props for slots 1-6. */
export interface SplitContentCheckListSlotProps {
  /** @polyLabel Check List Item 1 */
  checkItem1Text?: string;
  /** @polyLabel Check List Item 2 */
  checkItem2Text?: string;
  /** @polyLabel Check List Item 3 */
  checkItem3Text?: string;
  /** @polyLabel Check List Item 4 */
  checkItem4Text?: string;
  /** @polyLabel Check List Item 5 */
  checkItem5Text?: string;
  /** @polyLabel Check List Item 6 */
  checkItem6Text?: string;
}

/** Assemble a CTA from the flat props for a given slot (1-5). */
function getCta(props: SplitContentCtaSlotProps, slot: 1 | 2 | 3 | 4 | 5): RichTextCtaItem | null {
  const text = props[`cta${slot}Text`];
  const href = props[`cta${slot}Href`];
  const action = props[`cta${slot}Action`];
  const popupTargetId = props[`cta${slot}TargetPopupId`];
  if (!text || !hasCtaDestination(href, action, popupTargetId)) return null;
  return {
    text,
    href,
    type: props[`cta${slot}Type`],
    id: props[`cta${slot}Id`],
    ldAppendix: props[`cta${slot}LdAppendix`],
    behavior: props[`cta${slot}Behavior`],
    action,
    popupTargetId,
  };
}

/**
 * @polySemanticUsage About this block: a two-column layout that pairs media (image or video) on
 * one side with text content (headline, body, CTA) on the other. Use it for storytelling, feature
 * highlights, or explanatory sections where a visual and text pairing reinforces the message.
 * SplitContent is a SINGULAR content moment: exactly one media element paired with one
 * text column. It is never a repeating grid — three or more repeated items belong in a grid
 * block (ContentGrid/CardGrid), not in SplitContent. When the media+text pairing visually reads
 * as a self-contained card (including "video cards"), prefer a single-card CardGrid (columns=1,
 * one card, media beside content) over SplitContent.
 * Choose cta{N}Type by action type, not by appearance: registration actions (Sign up,
 * Start selling, Get started) take primary-button (Smile Orange fill); non-registration primary
 * actions (e.g. Enroll your brand) take dark-button (dark fill); secondary/discovery actions
 * (Learn more, Watch overview) take secondary-button (outlined). Use text-link or anchor-link
 * only for low-emphasis CTAs: they render as standalone arrow links (label with a trailing
 * arrow), not as buttons and not inline within the prose.
 * Link behavior: always leave cta{N}Behavior unset (same tab), even when that source link opens
 * in a new tab or is an External Link type. Migration never sets newTab; editors add it by hand
 * for the few exceptions. Never infer it from the destination host.
 * Video: a source video on the section (a play thumbnail or a "Watch" CTA that plays a video)
 * migrates to videoUrl, and the block renders a "Watch overview" button (secondary, after the
 * cta{N} buttons) that opens the video in a modal; the media slot shows imageUrl as a still image.
 * Never also put the video link in a cta{N}Href. Always set videoCtaText whenever videoUrl is
 * set: use the source button's label, or "Watch overview" when the source shows only a play
 * thumbnail. The built-in fallback label is English only, so a blank videoCtaText shows English
 * text on translated pages.
 */
export interface SplitContentProps extends SplitContentCtaSlotProps, SplitContentCheckListSlotProps {
  /**
   * Position of the media relative to the text content.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyTranslatable false
   */
  mediaPosition?: SplitContentMediaPosition;

  /**
   * Image URL
   * @polyType Url
   * @polyLabel Image URL
   */
  imageUrl?: string;

  /** Image alt text. Leave empty for decorative images. */
  imageAlt?: string;

  /**
   * @polyDefault fill
   * @polyHelp Fill covers the whole image area and may crop the edges; use it for photography. Fit shows the whole image with transparent space around it; use it for transparent images or images that must not be cropped.
   */
  imageFit?: SplitContentImageFit;

  /**
   * @polyDefault center
   * @polyHelp With Fill, picks which part of the image stays visible when it is cropped. With Fit, picks where the image sits in the image area.
   */
  imageAlignment?: ImageAlignment;

  /**
   * Browser loading hint for the image. Set by parent blocks that render SplitContent out of
   * view (for example a hidden tab panel); never authored in the CMS.
   * @polyIgnore
   */
  imageLoading?: 'eager' | 'lazy';

  /**
   * Video URL (YouTube or MP4). When set, a "Watch overview" button (label in videoCtaText)
   * joins the CTA buttons and opens the video in a modal. The media slot keeps showing imageUrl.
   * @polyType Url
   * @polyLabel Video URL
   * @polyHelp YouTube or MP4 link. Adds a "Watch overview" button (label set below) that opens the video.
   * @polyTranslatable false
   */
  videoUrl?: string;

  /**
   * Label for the "Watch overview" video button rendered when videoUrl is set.
   * @polyLabel Video Button Text
   * @polyHelp Label for the button that opens the video. Fill this in on every page so it gets translated. If blank, the button shows the English text "Watch overview".
   */
  videoCtaText?: string;

  // ---- Image overlays (image branch only) ----
  //
  // Give every overlay you populate an explicit overlay{N}Position and never reuse a position: an
  // overlay whose position is already taken by an earlier slot is silently dropped. Each slot has
  // its own distinct default (overlay1 bottom-right, overlay2 top-left), so partial positioning
  // is the trap — setting overlay1Position to top-left while leaving overlay2Position unset
  // collides with overlay2's default and drops overlay2. Position all overlays explicitly and
  // uniquely, or set no positions at all. Leave overlay{N}Width unset unless the source shows a
  // specific size; when set, use pixels.
  //

  /**
   * @polyType Url
   * @polyLabel Overlay 1 Image URL
   * @polyHelp Image positioned on top of the main image.
   */
  overlay1Url?: string;
  /**
   * @polyLabel Overlay 1 Alt Text
   */
  overlay1Alt?: string;
  /**
   * @polyType SelectList
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay1Position?: OverlayPosition;
  /**
   * @polyHelp Width in pixels, e.g. 240px. Leave blank to show the image at its designed size.
   * @polyTranslatable false
   */
  overlay1Width?: string;
  /**
   * @polyType Url
   * @polyLabel Overlay 2 Image URL
   * @polyHelp Image positioned on top of the main image.
   */
  overlay2Url?: string;
  /**
   * @polyLabel Overlay 2 Alt Text
   */
  overlay2Alt?: string;
  /**
   * @polyType SelectList
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay2Position?: OverlayPosition;
  /**
   * @polyHelp Width in pixels, e.g. 240px. Leave blank to show the image at its designed size.
   * @polyTranslatable false
   */
  overlay2Width?: string;

  /** Optional dark pill badge above the heading. */
  badgeText?: string;

  /**
   * Built-in icon rendered inside the badge (e.g. 'bolt').
   * @polyType SelectList
   * @polyOptions Cart=cart,Storefront=storefront,Package=package,Analytics=analytics,Star=star,Check=check,Arrow Forward=arrow-forward,Lock=lock,Menu=menu,Play=play,Search=search,Close=close,Globe=globe,Language=language,Arrow Left=arrow-left,Chevron Down=chevron-down,Chevron Right=chevron-right
   */
  badgeIcon?: CustomIconName;

  /**
   * Optional authored icon URL rendered inside the badge.
   * @polyType Url
   * @polyLabel Badge Icon URL
   */
  badgeIconUrl?: string;

  /** Optional eyebrow text above the heading (use instead of a badge). */
  eyebrow?: string;

  // ---- Content column (delegates to RichText) ----

  /**
   * Markdown content for the text column. Use `##` for the section heading
   * (38px Ember Modern Display Bold).
   * @polyType RichText
   */
  content?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

export const SplitContent: React.FC<SplitContentProps> = ({
  mediaPosition = 'right',
  imageUrl,
  imageAlt = '',
  imageFit,
  imageAlignment,
  imageLoading,
  videoUrl,
  videoCtaText,
  overlay1Url,
  overlay1Alt,
  overlay1Position = 'bottom-right',
  overlay1Width,
  overlay2Url,
  overlay2Alt,
  overlay2Position = 'top-left',
  overlay2Width,
  badgeText,
  badgeIcon,
  badgeIconUrl,
  eyebrow,
  content,
  checkItem1Text,
  checkItem2Text,
  checkItem3Text,
  checkItem4Text,
  checkItem5Text,
  checkItem6Text,
  cta1Text, cta1Href, cta1Type, cta1Id, cta1LdAppendix, cta1Behavior, cta1Action, cta1TargetPopupId,
  cta2Text, cta2Href, cta2Type, cta2Id, cta2LdAppendix, cta2Behavior, cta2Action, cta2TargetPopupId,
  cta3Text, cta3Href, cta3Type, cta3Id, cta3LdAppendix, cta3Behavior, cta3Action, cta3TargetPopupId,
  cta4Text, cta4Href, cta4Type, cta4Id, cta4LdAppendix, cta4Behavior, cta4Action, cta4TargetPopupId,
  cta5Text, cta5Href, cta5Type, cta5Id, cta5LdAppendix, cta5Behavior, cta5Action, cta5TargetPopupId,
  anchorId,
}) => {
  const hasEyebrowRow = Boolean(badgeText || eyebrow);

  // Check list items are rendered by the delegated RichText, so its slots are
  // forwarded untouched rather than reassembled here.
  const checkItemSlots = {
    checkItem1Text,
    checkItem2Text,
    checkItem3Text,
    checkItem4Text,
    checkItem5Text,
    checkItem6Text,
  };

  const ctaSlots: SplitContentCtaSlotProps = {
    cta1Text, cta1Href, cta1Type, cta1Id, cta1LdAppendix, cta1Behavior, cta1Action, cta1TargetPopupId,
    cta2Text, cta2Href, cta2Type, cta2Id, cta2LdAppendix, cta2Behavior, cta2Action, cta2TargetPopupId,
    cta3Text, cta3Href, cta3Type, cta3Id, cta3LdAppendix, cta3Behavior, cta3Action, cta3TargetPopupId,
    cta4Text, cta4Href, cta4Type, cta4Id, cta4LdAppendix, cta4Behavior, cta4Action, cta4TargetPopupId,
    cta5Text, cta5Href, cta5Type, cta5Id, cta5LdAppendix, cta5Behavior, cta5Action, cta5TargetPopupId,
  };

  const ctas = ([1, 2, 3, 4, 5] as const)
    .map(slot => getCta(ctaSlots, slot))
    .filter((cta): cta is RichTextCtaItem => cta !== null);

  const hasVideoCta = !!videoUrl;
  const linkCtas = ctas.filter((c) => isLinkCta(c.type));
  const buttonCtas = ctas.filter((c) => !isLinkCta(c.type));

  const contentSlot = (
    <div className="split-content__text">
      {hasEyebrowRow && (
        <div className="split-content__eyebrow-row">
          {badgeText && (
            <Badge size="sm" tone="dark" icon={badgeIcon} iconUrl={badgeIconUrl}>
              {badgeText}
            </Badge>
          )}
          {eyebrow && <span className="split-content__eyebrow">{eyebrow}</span>}
        </div>
      )}
      <RichText
        content={content ?? ''}
        fontSize="small"
        {...checkItemSlots}
      />
      {(linkCtas.length > 0 || buttonCtas.length > 0 || hasVideoCta) && (
        <div className="split-content__ctas">
          {linkCtas.map((cta, i) => (
            <ArrowLink
              key={`link-${i}`}
              href={cta.href}
              ctaId={cta.id}
              direction="right"
              behavior={cta.behavior}
              action={cta.action}
              popupTargetId={cta.popupTargetId}
            >
              {cta.text}
            </ArrowLink>
          ))}
          {(buttonCtas.length > 0 || hasVideoCta) && (
            <div className="split-content__cta-buttons">
              {buttonCtas.map((cta, i) => (
                <CtaButton
                  key={`btn-${i}`}
                  size="compact"
                  colorVariant={BUTTON_VARIANT[cta.type ?? 'primary-button'] ?? 'primary'}
                  href={cta.href}
                  id={cta.id}
                  ldAppendix={cta.ldAppendix}
                  behavior={cta.behavior}
                  action={cta.action}
                  popupTargetId={cta.popupTargetId}
                  text={cta.text}
                />
              ))}
              {hasVideoCta && (
                <CtaButton size="compact" colorVariant="secondary" href={videoUrl} action="video">
                  {videoCtaText || 'Watch overview'}
                </CtaButton>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const overlayProps = {
    overlay1Url, overlay1Alt, overlay1Position, overlay1Width,
    overlay2Url, overlay2Alt, overlay2Position, overlay2Width,
  };

  const mediaSlot = imageUrl ? (
    <div className="split-content__media">
      <Image
        url={imageUrl}
        alt={imageAlt}
        objectFit={imageFit === 'fit' ? 'contain' : 'cover'}
        alignment={imageAlignment}
        loading={imageLoading}
        {...overlayProps}
      />
    </div>
  ) : null;

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className={`split-content split-content--media-${mediaPosition}`}
      data-poly-block="SplitContent"
    >
      <div className="split-content__inner">
        {contentSlot}
        {mediaSlot}
      </div>
    </section>
  );
};
